/**
 * useDebouncedRunState
 *
 * Subscribes to solutionRunStore and returns a debounced SolutionRunSnapshot.
 * Returns null when the store is idle (no active run) or during the 400ms
 * quiet period after the last change.
 *
 * WHY debounce (DR-14): The InvokeSolutionOverlay can change rapidly (every
 * keystroke). Debouncing at 400ms prevents excessive renders in the ESA panel
 * card while still feeling live to the user.
 */

import { useEffect, useRef, useState } from 'react';
import { getSolutionRunStore } from '../../Canvas/store/solutionRunStore';
import type { SolutionRunSnapshot } from '../../Canvas/types/solutionRun';
import { RUN_STATE_DEBOUNCE_MS } from '../../../constants/timing';

export function useDebouncedRunState(): SolutionRunSnapshot | null {
  const [snapshot, setSnapshot] = useState<SolutionRunSnapshot | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    // Bootstrap: read the current state immediately on mount.
    // SolutionRunCard mounts AFTER setConversationId + setEsaPanelMode('chat') have
    // already fired, so the store is already non-idle by the time the subscription
    // is registered. Without this read, the card would stay blank until the next
    // state change (first keystroke).
    const initial = getSolutionRunStore().getState();
    if (initial.status !== 'idle') {
      setSnapshot(initial.getSnapshot());
    }

    const unsubscribe = getSolutionRunStore().subscribe(state => {
      if (state.status === 'idle') {
        // Cancel any pending update and clear immediately on reset
        if (timerRef.current !== null) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        if (mountedRef.current) setSnapshot(null);
        return;
      }

      // Debounce non-idle updates
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        if (!mountedRef.current) return;
        const current = getSolutionRunStore().getState();
        if (current.status === 'idle') {
          setSnapshot(null);
        } else {
          setSnapshot(current.getSnapshot());
        }
      }, RUN_STATE_DEBOUNCE_MS);
    });

    return () => {
      mountedRef.current = false;
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      unsubscribe();
    };
  }, []);

  return snapshot;
}
